export const checkEnvironment = () => {
    // 値を安全に取得する関数
    const safeGet = (getter, defaultValue = false) => {
        try {
            const value = getter();
            return value !== undefined && value !== null ? value : defaultValue;
        } catch (error) {
            return defaultValue;
        }
    };

    // 安全にuserAgentを取得
    const userAgent = safeGet(() => navigator.userAgent, "");
    const platform = safeGet(() => navigator.platform, "");
    const maxTouchPoints = safeGet(() => navigator.maxTouchPoints, 0);
    
    // モバイルデバイスの検出 (チェックのみで非対応判断には使わない)
    const isMobile = safeGet(() => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent), false);
    const isIPad = safeGet(() => {
        return /iPad/i.test(userAgent) || 
               (platform === 'MacIntel' && maxTouchPoints > 1 && !window.MSStream);
    }, false);
    
    // ブラウザとバージョンの検出
    const getBrowserInfo = () => {
        // Chrome
        const chromeMatch = userAgent.match(/Chrome\/(\d+)/i);
        if (chromeMatch && !/Edge|Edg/i.test(userAgent)) {
            return { name: 'Chrome', version: parseInt(chromeMatch[1], 10), minVersion: 60 };
        }
        
        // Safari
        const safariMatch = userAgent.match(/Version\/(\d+)/i) && /Safari/i.test(userAgent) && !/Chrome|Edge|Edg/i.test(userAgent);
        if (safariMatch) {
            const versionMatch = userAgent.match(/Version\/(\d+)/i);
            return { name: 'Safari', version: versionMatch ? parseInt(versionMatch[1], 10) : 0, minVersion: 10 };
        }
        
        // Firefox
        const firefoxMatch = userAgent.match(/Firefox\/(\d+)/i);
        if (firefoxMatch) {
            return { name: 'Firefox', version: parseInt(firefoxMatch[1], 10), minVersion: 60 };
        }
        
        // Edge
        const edgeMatchLegacy = userAgent.match(/Edge\/(\d+)/i);
        if (edgeMatchLegacy) {
            return { name: 'Edge Legacy', version: parseInt(edgeMatchLegacy[1], 10), minVersion: 15 };
        }
        
        const edgeMatch = userAgent.match(/Edg\/(\d+)/i);
        if (edgeMatch) {
            return { name: 'Edge', version: parseInt(edgeMatch[1], 10), minVersion: 80 };
        }
        
        return { name: 'Unknown', version: 0, minVersion: 0 };
    };
    
    const browserInfo = getBrowserInfo();
    
    // ブラウザの検出
    const isChrome = safeGet(() => /Chrome/i.test(userAgent) && !/Edge|Edg/i.test(userAgent), false);
    const isSafari = safeGet(() => /Safari/i.test(userAgent) && !/Chrome|Edge|Edg/i.test(userAgent), false);
    const isFirefox = safeGet(() => /Firefox/i.test(userAgent), false);
    const isEdge = safeGet(() => /Edge|Edg/i.test(userAgent), false);
    const isSupportedBrowser = isChrome || isSafari || isFirefox || isEdge;
    
    // ブラウザバージョンチェック
    const isSupportedBrowserVersion = browserInfo.version >= browserInfo.minVersion;
    
    // 古いIEの検出
    const isOldBrowser = safeGet(() => /MSIE|Trident/i.test(userAgent), false);
    
    // 画面サイズの確認 (チェックのみで非対応判断には使わない)
    const screenWidth = safeGet(() => window.screen.width, 0);
    const screenHeight = safeGet(() => window.screen.height, 0);
    const hasMinimumScreenSize = screenWidth >= 768 && screenHeight >= 600;
    
    // 環境がサポートされているかどうかを判断
    // モバイルデバイスも含めてサポート対象に変更、画面サイズの条件も除外
    const isUnsupported = isOldBrowser || !isSupportedBrowser || !isSupportedBrowserVersion;
    
    // 詳細な理由を判定
    let unsupportedReason = '';
    if (isOldBrowser) unsupportedReason = '古いブラウザ';
    else if (!isSupportedBrowser) unsupportedReason = '未サポートのブラウザ';
    else if (!isSupportedBrowserVersion) unsupportedReason = `古いバージョンのブラウザ (${browserInfo.name} ${browserInfo.version}, 必要: ${browserInfo.minVersion}以上)`;
    
    // 判定結果を記号で表現
    const getResultSymbol = (isOk) => isOk ? '◯' : '×';
    
    // コンソールに環境チェック結果を出力
    console.group('環境チェック結果');
    console.log(`実行環境: ${userAgent}`);
    console.log(`プラットフォーム: ${platform}`);
    console.log(`ブラウザ: ${browserInfo.name} ${browserInfo.version} (最小要求バージョン: ${browserInfo.minVersion})`);
    console.log(`ブラウザ対応: ${getResultSymbol(isSupportedBrowser)} (Chrome: ${getResultSymbol(isChrome)}, Safari: ${getResultSymbol(isSafari)}, Firefox: ${getResultSymbol(isFirefox)}, Edge: ${getResultSymbol(isEdge)})`);
    console.log(`ブラウザバージョン対応: ${getResultSymbol(isSupportedBrowserVersion)}`);
    console.log(`モバイル: ${isMobile ? (isIPad ? 'iPad' : 'スマートフォン等') : 'デスクトップ'} (対応: ◯)`);
    console.log(`画面サイズ: ${getResultSymbol(hasMinimumScreenSize)} (${screenWidth}x${screenHeight}) (判定には影響しない)`);
    console.log(`古いブラウザ: ${getResultSymbol(!isOldBrowser)}`);
    console.log(`総合判定: ${getResultSymbol(!isUnsupported)} ${isUnsupported ? '(' + unsupportedReason + ')' : ''}`);
    console.groupEnd();
    
    return {
        isUnsupported,
        message: 'お使いの機器はこのアプリケーションの実行に適さないようです。\nお手数ですが受付にお声がけいただき、医療機関用iPadを受け取ってチェックを進めてください。',
        details: {
            isMobile,
            isIPad,
            userAgent: userAgent || "権限がありません",
            platform: platform || "権限がありません",
            browserInfo: {
                name: browserInfo.name,
                version: browserInfo.version,
                minVersion: browserInfo.minVersion,
                isChrome,
                isSafari,
                isFirefox,
                isEdge,
                isOldBrowser,
                isSupportedBrowser,
                isSupportedBrowserVersion
            },
            screenInfo: {
                width: screenWidth || "権限がありません",
                height: screenHeight || "権限がありません",
                hasMinimumScreenSize
            },
            unsupportedReason: isUnsupported ? unsupportedReason : '環境は適合しています'
        }
    };
}; 